import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api";
import { ImagePickerButton, ImagePreview } from "../components/ImagePicker";
import { containsHangul } from "../lib/hangul";
import type {
  DeckLanguage,
  KoreanLookupSense,
  Note,
  NoteSide,
  SwedishLookupSense,
  UpdateNoteRequest,
} from "@glosor/shared";

function blankSide(): NoteSide {
  return { text: null, image: null };
}

function isValidSide(s: NoteSide): boolean {
  return (s.text !== null && s.text.trim().length > 0) || (s.image !== null && s.image.length > 0);
}

function sameSide(a: NoteSide, b: NoteSide): boolean {
  const text = (s: NoteSide) => (s.text && s.text.length > 0 ? s.text : null);
  const image = (s: NoteSide) => (s.image && s.image.length > 0 ? s.image : null);
  return text(a) === text(b) && image(a) === image(b);
}

interface Props {
  mode: "create" | "edit";
}

export default function NoteEditor({ mode }: Props) {
  const { deckId, noteId } = useParams();
  const dId = Number(deckId);
  const nId = noteId ? Number(noteId) : null;
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const returnTo = (location.state as { from?: string } | null)?.from ?? `/decks/${dId}`;

  const notes = useQuery({
    queryKey: ["notes", dId],
    queryFn: () => api.listNotes(dId),
    enabled: mode === "edit" && Number.isInteger(dId),
  });

  const decks = useQuery({ queryKey: ["decks"], queryFn: () => api.listDecks() });
  const deckLanguage = decks.data?.find((d) => d.id === dId)?.language ?? null;

  const [front, setFront] = useState<NoteSide>(blankSide);
  const [back, setBack] = useState<NoteSide>(blankSide);
  const [description, setDescription] = useState<NoteSide>(blankSide);
  const [bidirectional, setBidirectional] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addedCount, setAddedCount] = useState(0);
  const frontRef = useRef<HTMLTextAreaElement | null>(null);
  // Snapshot of the note the form was last prefilled from, used to send only
  // the fields the user actually changed.
  const loadedNoteRef = useRef<Note | null>(null);
  // Once the user has touched the form, background refetches of the notes list
  // must not clobber their in-progress edits by re-running the prefill.
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (mode === "edit" && notes.data && nId !== null && !dirtyRef.current) {
      const existing = notes.data.find((n) => n.id === nId);
      if (existing) {
        loadedNoteRef.current = existing;
        setFront(existing.front);
        setBack(existing.back);
        setDescription(existing.description);
        setBidirectional(existing.bidirectional);
      }
    }
  }, [mode, notes.data, nId]);

  const editFront = (s: NoteSide) => {
    dirtyRef.current = true;
    setFront(s);
  };
  const editBack = (s: NoteSide) => {
    dirtyRef.current = true;
    setBack(s);
  };
  const editDescription = (s: NoteSide) => {
    dirtyRef.current = true;
    setDescription(s);
  };
  const editBidirectional = (b: boolean) => {
    dirtyRef.current = true;
    setBidirectional(b);
  };

  useEffect(() => {
    if (mode === "create") frontRef.current?.focus();
  }, [mode]);

  const create = useMutation({
    mutationFn: () => api.createNote({ deckId: dId, front, back, description, bidirectional }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notes", dId] });
      qc.invalidateQueries({ queryKey: ["decks"] });
      setFront(blankSide());
      setBack(blankSide());
      setDescription(blankSide());
      setError(null);
      setAddedCount((n) => n + 1);
      dirtyRef.current = false;
      frontRef.current?.focus();
    },
  });

  const update = useMutation({
    mutationFn: () => {
      // Only send what changed relative to the prefilled snapshot, so a stale
      // snapshot of one side can never silently revert the other.
      const loaded = loadedNoteRef.current;
      const body: UpdateNoteRequest = {};
      if (!loaded || !sameSide(front, loaded.front)) body.front = front;
      if (!loaded || !sameSide(back, loaded.back)) body.back = back;
      if (!loaded || !sameSide(description, loaded.description)) body.description = description;
      if (!loaded || bidirectional !== loaded.bidirectional) body.bidirectional = bidirectional;
      return api.updateNote(nId!, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notes", dId] });
      // Drop the cached study queue so returning to Study can't flash the
      // pre-edit card content while it refetches.
      qc.removeQueries({ queryKey: ["study", dId] });
      navigate(returnTo);
    },
  });

  const saveRef = useRef<() => void>(() => {});
  saveRef.current = function onSave() {
    if (create.isPending || update.isPending) return;
    setError(null);
    if (!isValidSide(front)) {
      setError("Front needs text or an image");
      return;
    }
    if (!isValidSide(back)) {
      setError("Back needs text or an image");
      return;
    }
    if (mode === "create") create.mutate();
    else update.mutate();
  };

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        saveRef.current();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5">
      <header className="flex items-center gap-3">
        <button onClick={() => navigate(returnTo)} className="btn-secondary">←</button>
        <h1 className="text-2xl font-semibold flex-1">{mode === "create" ? "New card" : "Edit card"}</h1>
        {mode === "create" && addedCount > 0 && (
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Added <span className="text-slate-800 dark:text-slate-200 font-semibold">{addedCount}</span>
          </div>
        )}
      </header>

      <SideEditor label="Front" side={front} onChange={editFront} textareaRef={frontRef} />
      <DictionaryLookupPanel
        language={deckLanguage}
        frontText={front.text ?? ""}
        back={back}
        setBack={editBack}
        description={description}
        setDescription={editDescription}
      />
      <SideEditor label="Back" side={back} onChange={editBack} />
      <SideEditor label="Description" side={description} onChange={editDescription} optional />

      <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
        <input
          type="checkbox"
          checked={bidirectional}
          onChange={(e) => editBidirectional(e.target.checked)}
          className="mt-1 accent-sky-500"
        />
        <span>
          <span className="text-slate-900 dark:text-slate-100">Also study back → front</span>
          <span className="block text-xs text-slate-500 dark:text-slate-400">
            Uncheck for a one-way card (front → back only).
          </span>
        </span>
      </label>

      {error && <div className="text-rose-500 dark:text-rose-400 text-sm">{error}</div>}

      <button
        onClick={() => saveRef.current()}
        disabled={create.isPending || update.isPending}
        className="btn-primary w-full text-lg py-3"
      >
        {mode === "create" ? "Add card" : "Save"}
        <span className="ml-2 text-xs text-sky-100/70">⌘↵</span>
      </button>
    </div>
  );
}

function SideEditor({
  label,
  side,
  onChange,
  textareaRef,
  optional,
}: {
  label: string;
  side: NoteSide;
  onChange: (s: NoteSide) => void;
  textareaRef?: React.Ref<HTMLTextAreaElement>;
  optional?: boolean;
}) {
  return (
    <div className="card-surface p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        <ImagePickerButton
          value={side.image}
          onChange={(image) => onChange({ ...side, image })}
        />
      </div>
      <ImagePreview
        value={side.image}
        onChange={(image) => onChange({ ...side, image })}
      />
      <textarea
        ref={textareaRef}
        value={side.text ?? ""}
        onChange={(e) => onChange({ ...side, text: e.target.value })}
        placeholder={optional ? "Text (optional)" : "Text (optional if image)"}
        rows={3}
        className="input"
      />
    </div>
  );
}

// Routes the lookup panel to the right dictionary for the deck's language.
// `ko` shows the Korean panel when the front text contains Hangul, `sv` shows
// the Swedish panel, and `null` (none) disables both.
function DictionaryLookupPanel({
  language,
  ...rest
}: {
  language: DeckLanguage | null;
  frontText: string;
  back: NoteSide;
  setBack: (s: NoteSide) => void;
  description: NoteSide;
  setDescription: (s: NoteSide) => void;
}) {
  if (language === "sv") return <SwedishLookupPanel {...rest} />;
  if (language === "ko") return <KoreanLookupPanel {...rest} />;
  return null;
}

function KoreanLookupPanel({
  frontText,
  back,
  setBack,
  description,
  setDescription,
}: {
  frontText: string;
  back: NoteSide;
  setBack: (s: NoteSide) => void;
  description: NoteSide;
  setDescription: (s: NoteSide) => void;
}) {
  const trimmed = frontText.trim();
  const visible = containsHangul(trimmed);

  const [suggestions, setSuggestions] = useState<KoreanLookupSense[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cached, setCached] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const reqIdRef = useRef(0);
  const panelOwnsBackRef = useRef(false);
  const panelOwnsDescriptionRef = useRef(false);
  const notConfiguredRef = useRef(false);

  // Reset panel ownership and selections when the form is cleared.
  useEffect(() => {
    if (trimmed.length === 0) {
      panelOwnsBackRef.current = false;
      panelOwnsDescriptionRef.current = false;
      setSuggestions([]);
      setSelectedIds(new Set());
      setError(null);
      setCached(false);
    }
  }, [trimmed]);

  useEffect(() => {
    if (!visible) return;
    if (notConfiguredRef.current) return;

    const handle = setTimeout(() => {
      const myReqId = ++reqIdRef.current;
      const ac = new AbortController();
      setLoading(true);
      setError(null);
      api
        .lookupKorean(trimmed, { signal: ac.signal, refresh: refreshKey > 0 })
        .then((result) => {
          if (reqIdRef.current !== myReqId) return;
          setSuggestions(result.senses);
          setCached(result.cached);
          setLoading(false);
          // Drop any selected ids that no longer exist in the new result.
          setSelectedIds((prev) => {
            const validIds = new Set(result.senses.map((s) => s.id));
            const next = new Set<string>();
            for (const id of prev) if (validIds.has(id)) next.add(id);
            return next;
          });
        })
        .catch((e: unknown) => {
          if (reqIdRef.current !== myReqId) return;
          if (ac.signal.aborted) return;
          setLoading(false);
          if (e instanceof ApiError) {
            if (e.status === 503 && e.message.toLowerCase().includes("not set")) {
              notConfiguredRef.current = true;
              setError("Korean dictionary is not configured on the server");
            } else {
              setError(e.message);
            }
          } else {
            setError("Lookup failed");
          }
          setSuggestions([]);
        });
      return () => ac.abort();
    }, 300);

    return () => clearTimeout(handle);
  }, [trimmed, visible, refreshKey]);

  if (!visible) return null;

  function toggle(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);

    const picked = suggestions.filter((s) => next.has(s.id));

    const joinedBack = picked.map((s) => s.english).join(", ");
    panelOwnsBackRef.current = true;
    setBack({ ...back, text: joinedBack.length > 0 ? joinedBack : null });

    const seen = new Set<string>();
    const descLines: string[] = [];
    for (const s of picked) {
      const parts: string[] = [];
      if (s.origin) parts.push(s.origin);
      if (s.pronunciation) parts.push(`[${s.pronunciation}]`);
      if (s.koreanDefinition) parts.push(s.koreanDefinition);
      for (const line of parts) {
        if (!seen.has(line)) {
          seen.add(line);
          descLines.push(line);
        }
      }
    }
    const joinedDesc = descLines.join("\n");
    panelOwnsDescriptionRef.current = true;
    setDescription({ ...description, text: joinedDesc.length > 0 ? joinedDesc : null });
  }

  function onRefresh() {
    notConfiguredRef.current = false;
    setRefreshKey((n) => n + 1);
  }

  return (
    <div className="card-surface p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-slate-500">
          Korean dictionary{" "}
          {cached && !loading && <span className="text-slate-400 dark:text-slate-600">(cached)</span>}
        </div>
        <button
          onClick={onRefresh}
          className="text-xs text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300"
          disabled={loading}
        >
          Refresh
        </button>
      </div>
      {loading && <div className="text-sm text-slate-500 dark:text-slate-400">Looking up…</div>}
      {error && <div className="text-sm text-rose-500 dark:text-rose-400">{error}</div>}
      {!loading && !error && suggestions.length === 0 && (
        <div className="text-sm text-slate-500">No suggestions</div>
      )}
      <ul className="space-y-1">
        {suggestions.map((s) => (
          <li key={s.id}>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIds.has(s.id)}
                onChange={() => toggle(s.id)}
                className="mt-1 accent-sky-500"
              />
              <span>
                <span className="text-slate-900 dark:text-slate-100">{s.english}</span>
                {s.origin && (
                  <span className="ml-2 text-sm text-amber-600 dark:text-amber-300">
                    {s.origin}
                  </span>
                )}
                {s.pronunciation && (
                  <span className="ml-2 text-xs text-slate-500">[{s.pronunciation}]</span>
                )}
                {s.partOfSpeech && (
                  <span className="ml-2 text-xs text-slate-500">{s.partOfSpeech}</span>
                )}
                {s.englishDefinition && (
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {s.englishDefinition}
                  </div>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Swedish→Swedish lookup against the bundled LEXIN dictionary. Unlike KRDict
// this is local (no network/cache/refresh). Checking a sense fills the Back
// field with the Swedish definition(s) and the Description with examples,
// English translation and pronunciation.
function SwedishLookupPanel({
  frontText,
  back,
  setBack,
  description,
  setDescription,
}: {
  frontText: string;
  back: NoteSide;
  setBack: (s: NoteSide) => void;
  description: NoteSide;
  setDescription: (s: NoteSide) => void;
}) {
  const trimmed = frontText.trim();
  const visible = trimmed.length > 0;

  const [suggestions, setSuggestions] = useState<SwedishLookupSense[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (trimmed.length === 0) {
      setSuggestions([]);
      setSelectedIds(new Set());
      setError(null);
    }
  }, [trimmed]);

  useEffect(() => {
    if (!visible) return;
    const handle = setTimeout(() => {
      const myReqId = ++reqIdRef.current;
      const ac = new AbortController();
      setLoading(true);
      setError(null);
      api
        .lookupSwedish(trimmed, { signal: ac.signal })
        .then((result) => {
          if (reqIdRef.current !== myReqId) return;
          setSuggestions(result.senses);
          setLoading(false);
          setSelectedIds((prev) => {
            const validIds = new Set(result.senses.map((s) => s.id));
            const next = new Set<string>();
            for (const id of prev) if (validIds.has(id)) next.add(id);
            return next;
          });
        })
        .catch((e: unknown) => {
          if (reqIdRef.current !== myReqId) return;
          if (ac.signal.aborted) return;
          setLoading(false);
          setError(e instanceof ApiError ? e.message : "Lookup failed");
          setSuggestions([]);
        });
      return () => ac.abort();
    }, 300);
    return () => clearTimeout(handle);
  }, [trimmed, visible]);

  if (!visible) return null;

  function toggle(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);

    const picked = suggestions.filter((s) => next.has(s.id));

    const joinedBack = picked
      .map((s) => s.definition)
      .filter((d) => d.length > 0)
      .join("; ");
    setBack({ ...back, text: joinedBack.length > 0 ? joinedBack : null });

    const seen = new Set<string>();
    const descLines: string[] = [];
    for (const s of picked) {
      const parts: string[] = [];
      if (s.english) parts.push(s.english);
      if (s.pronunciation) parts.push(`[${s.pronunciation}]`);
      for (const ex of s.examples) parts.push(`ex: ${ex}`);
      for (const line of parts) {
        if (!seen.has(line)) {
          seen.add(line);
          descLines.push(line);
        }
      }
    }
    const joinedDesc = descLines.join("\n");
    setDescription({ ...description, text: joinedDesc.length > 0 ? joinedDesc : null });
  }

  return (
    <div className="card-surface p-4 space-y-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">Swedish dictionary</div>
      {loading && <div className="text-sm text-slate-500 dark:text-slate-400">Looking up…</div>}
      {error && <div className="text-sm text-rose-500 dark:text-rose-400">{error}</div>}
      {!loading && !error && suggestions.length === 0 && (
        <div className="text-sm text-slate-500">No suggestions</div>
      )}
      <ul className="space-y-1">
        {suggestions.map((s) => (
          <li key={s.id}>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIds.has(s.id)}
                onChange={() => toggle(s.id)}
                className="mt-1 accent-sky-500"
              />
              <span>
                <span className="text-slate-900 dark:text-slate-100">{s.definition}</span>
                {s.partOfSpeech && (
                  <span className="ml-2 text-xs text-slate-500">{s.partOfSpeech}</span>
                )}
                {s.pronunciation && (
                  <span className="ml-2 text-xs text-slate-500">[{s.pronunciation}]</span>
                )}
                {s.english && (
                  <span className="ml-2 text-sm text-amber-600 dark:text-amber-300">
                    {s.english}
                  </span>
                )}
                {s.examples.length > 0 && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 italic">
                    {s.examples[0]}
                  </div>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
